import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConversationStoreShallow } from 'src/store/conversationStore';

import { notify } from '@speeki/global-ui-components';

import {
    DeleteFilesDocument,
    NamedUpload,
    UploadFilesDocument,
    UploadFilesMutation,
} from '@graphql/generated/graphql';

import { useMutation } from '@apollo/client';
import { MAX_FILE_SIZE_MB, MAX_REQUEST_FILES_SIZE_MB } from '@shared/constants';

export const useReportUpload = (caseUuid: string) => {
    const { t } = useTranslation();

    const [files, setFiles] = useState<NamedUpload[]>([]);
    const [uploadedFiles, setUploadedFiles] = useState<
        UploadFilesMutation['file']
    >([]);
    const [uploadedFilesSizeMb, setUploadedFilesSizeMb] = useState(0);

    const { setConversationLoading } = useConversationStoreShallow([
        'setConversationLoading',
    ]);

    const [uploadFile, { loading }] = useMutation(UploadFilesDocument, {
        context: {
            notifyOnError: true,
        },
        notifyOnNetworkStatusChange: true,
        onCompleted: (data) => {
            setUploadedFiles((prevState) => [...prevState, ...data.file]);
            setConversationLoading(false);
        },
        onError: () => {},
    });

    const [deleteFile] = useMutation(DeleteFilesDocument, {
        context: {
            notifyOnError: true,
        },
        onCompleted: (data) => {
            setUploadedFiles((prev) =>
                prev.filter((_) => _.uuid !== data.deleteFiles?.[0]),
            );
            setConversationLoading(false);
        },
        onError: () => setConversationLoading(false),
    });

    const removeFile = async (fileId: string) => {
        setConversationLoading(true);
        void deleteFile({
            variables: { input: { caseUuid, files: [{ uuid: fileId }] } },
        });
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        e.persist();
        setConversationLoading(true);
        const fileArray: NamedUpload[] = [
            ...Array.from(e.target.files ?? []),
        ].map((file) => ({
            file: file,
            fileKey: file.name,
        }));

        let summaryFileSizeMb = uploadedFilesSizeMb;
        let filesValid = true;

        for (const { file } of fileArray) {
            const fileSizeMb = file?.size / (1024 * 1024);
            if (fileSizeMb > MAX_FILE_SIZE_MB) {
                notify(
                    `${t('TOO_BIG_CHAT_FILE')} ${MAX_FILE_SIZE_MB}MB`,
                ).ERROR();
                filesValid = false;
                break;
            }

            if ((summaryFileSizeMb += fileSizeMb) > MAX_REQUEST_FILES_SIZE_MB) {
                notify(
                    t(
                        `${t(
                            'TOO_BIG_REQUEST_CHAT_FILE',
                        )} ${MAX_REQUEST_FILES_SIZE_MB}MB`,
                    ),
                ).ERROR();
                filesValid = false;
                break;
            }
        }

        if (!filesValid) {
            setConversationLoading(false);
            return;
        }

        setFiles((prev) => [...prev, ...fileArray]);

        fileArray.length &&
            (await uploadFile({
                variables: { input: { caseUuid, files: fileArray } },
            }));
        setFiles([]);
        setConversationLoading(false);
    };

    useEffect(() => {
        const sizeMb = uploadedFiles.reduce(
            (acc, curr) => acc + (curr.size ?? 0) / (1024 * 1024),
            0,
        );
        setUploadedFilesSizeMb(sizeMb);
    }, [uploadedFiles]);

    return {
        files,
        handleUpload,
        loading,
        removeFile,
        uploadedFiles,
    };
};
